import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './Toast';
import { useConfirm } from './ConfirmModal';
import { colors } from '../theme/tokens';

const STATUS_LABELS = {
  stable: 'Stable Client',
  unstable: 'Unstable Client',
  competitor: 'Buying from Competitor',
  lead: 'New Lead',
  technical: 'Technical Difficulty',
  inactive: 'Inactive'
};

// Accept both human-readable and raw status values from the CSV
const STATUS_MAP = {
  'stable client': 'stable',
  'stable': 'stable',
  'unstable client': 'unstable',
  'unstable': 'unstable',
  'buying from competitor': 'competitor',
  'competitor': 'competitor',
  'new lead': 'lead',
  'lead': 'lead',
  'technical difficulty': 'technical',
  'technical': 'technical',
  'inactive': 'inactive'
};

const parseLocation = (locStr) => {
  if (!locStr) return null;
  const parts = locStr.toString().split(',').map(s => s.trim());
  if (parts.length < 2) return null;
  const lat = parseFloat(parts[0]);
  const lng = parseFloat(parts[1]);
  if (isNaN(lat) || isNaN(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
};

const validateRow = (row, index) => {
  const errors = [];

  const clientName = (
    row['Client Name'] || row['client name'] || row['ClientName'] ||
    row['CLIENT NAME'] || row['name'] || ''
  ).toString().trim();

  const statusRaw = (
    row['Status'] || row['status'] || row['STATUS'] || ''
  ).toString().trim().toLowerCase();

  const locationRaw = (
    row['Location'] || row['location'] || row['LOCATION'] || ''
  ).toString().trim();

  if (!clientName) errors.push('Client name is required');

  const status = STATUS_MAP[statusRaw];
  if (!status) {
    errors.push(
      `Unknown status "${statusRaw}" — use: New Lead, Stable Client, Unstable Client, Buying from Competitor, Technical Difficulty`
    );
  }

  const location = parseLocation(locationRaw);
  if (!location) {
    errors.push(`Invalid location "${locationRaw}" — use format: -26.277, 28.438`);
  }

  return {
    id: `row-${index}-${Date.now()}`,
    clientName,
    status: status || 'lead',
    locationStr: locationRaw,
    location,
    errors: errors.length > 0 ? errors : null,
    importStatus: null
  };
};

const LeadsImport = () => {
  const { token } = useAuth();
  const showToast = useToast();
  const confirm = useConfirm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const downloadTemplate = () => {
    const csv = [
      ['Client Name', 'Status', 'Location'],
      ['ProAuto Rubber', 'New Lead', '-26.277, 28.438'],
      ['Cape Seals CC', 'Stable Client', '-33.925, 18.424'],
      ['Durban AutoParts', 'Buying from Competitor', '-29.858, 31.021'],
      ['PretoriaSeal Co', 'Unstable Client', '-25.747, 28.188']
    ].map(r => r.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', 'leads_import_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(l => l.trim());
    if (lines.length < 2) return [];

    // Handle quoted fields
    const splitLine = (line) => {
      const result = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') { inQuotes = !inQuotes; }
        else if (ch === ',' && !inQuotes) { result.push(current.trim()); current = ''; }
        else { current += ch; }
      }
      result.push(current.trim());
      return result;
    };

    const headers = splitLine(lines[0]);
    return lines.slice(1).map(line => {
      const values = splitLine(line);
      const obj = {};
      headers.forEach((h, i) => { obj[h.trim()] = (values[i] || '').trim(); });
      return obj;
    });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) { showToast('Please upload a CSV file', 'warning'); return; }

    setLoading(true);
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const rawRows = parseCSV(event.target.result);
        if (rawRows.length === 0) { showToast('CSV file is empty or has no data rows', 'warning'); setLoading(false); return; }
        const parsed = rawRows.map((r, i) => validateRow(r, i));
        setRows(parsed);
        const errCount = parsed.filter(r => r.errors).length;
        showToast(
          `Loaded ${parsed.length} rows — ${parsed.length - errCount} valid, ${errCount} with errors`,
          errCount > 0 ? 'warning' : 'success'
        );
      } catch (err) {
        showToast('Failed to parse CSV: ' + err.message, 'error');
      } finally {
        setLoading(false);
      }
    };
    reader.onerror = () => { showToast('Failed to read file', 'error'); setLoading(false); };
    reader.readAsText(file);
    e.target.value = '';
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditForm({
      clientName: row.clientName,
      status: row.status,
      locationStr: row.locationStr
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditForm({}); };

  const saveEdit = () => {
    const location = parseLocation(editForm.locationStr);
    const errors = [];
    if (!editForm.clientName.trim()) errors.push('Client name is required');
    if (!location) errors.push(`Invalid location — use format: -26.277, 28.438`);

    setRows(prev => prev.map(r => r.id === editingId ? {
      ...r,
      clientName: editForm.clientName.trim(),
      status: editForm.status,
      locationStr: editForm.locationStr,
      location,
      errors: errors.length > 0 ? errors : null,
      importStatus: errors.length > 0 ? null : r.importStatus
    } : r));
    setEditingId(null);
    setEditForm({});
  };

  const deleteRow = async (id) => {
    const ok = await confirm('Remove this row from the import list?', {
      title: 'Remove Row', icon: '🗑️', confirmText: 'Remove', danger: true
    });
    if (ok) setRows(prev => prev.filter(r => r.id !== id));
  };

  const importSingle = async (row) => {
    if (row.errors) { showToast('Fix errors before importing this row', 'warning'); return; }
    if (row.importStatus === 'imported') { showToast('Already imported', 'info'); return; }

    setImporting(true);
    try {
      await axios.post(
        `${API_URL}/api/client-markers/import-bulk`,
        { leads: [{ clientName: row.clientName, status: row.status, location: row.location }] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRows(prev => prev.map(r => r.id === row.id ? { ...r, importStatus: 'imported' } : r));
      showToast(`"${row.clientName}" imported successfully`, 'success');
    } catch (err) {
      showToast('Import failed: ' + (err.response?.data?.error || err.message), 'error');
      setRows(prev => prev.map(r => r.id === row.id ? { ...r, importStatus: 'error' } : r));
    } finally {
      setImporting(false);
    }
  };

  const importAll = async () => {
    const toImport = rows.filter(r => !r.errors && r.importStatus !== 'imported');
    if (toImport.length === 0) { showToast('No valid rows to import', 'warning'); return; }

    const ok = await confirm(`Import ${toImport.length} lead${toImport.length !== 1 ? 's' : ''} into the map?`, {
      title: 'Import All Leads', icon: '📤', confirmText: 'Import All'
    });
    if (!ok) return;

    setImporting(true);
    try {
      const leads = toImport.map(r => ({ clientName: r.clientName, status: r.status, location: r.location }));
      const res = await axios.post(
        `${API_URL}/api/client-markers/import-bulk`,
        { leads },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { results } = res.data;
      setRows(prev => prev.map(r =>
        (!r.errors && r.importStatus !== 'imported') ? { ...r, importStatus: 'imported' } : r
      ));
      showToast(
        `Import complete — ✅ ${results.success} imported${results.failed > 0 ? `, ❌ ${results.failed} failed` : ''}`,
        results.failed > 0 ? 'warning' : 'success'
      );
    } catch (err) {
      showToast('Import failed: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setImporting(false);
    }
  };

  const clearAll = async () => {
    const ok = await confirm('Clear all rows from the preview?', {
      title: 'Clear Table', icon: '🗑️', confirmText: 'Clear', danger: true
    });
    if (ok) { setRows([]); showToast('Table cleared', 'info'); }
  };

  const stats = {
    total: rows.length,
    valid: rows.filter(r => !r.errors).length,
    errors: rows.filter(r => r.errors).length,
    imported: rows.filter(r => r.importStatus === 'imported').length,
    pending: rows.filter(r => !r.errors && r.importStatus !== 'imported').length
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>📥 Import Leads</h2>
        <p style={styles.subtitle}>Upload a CSV file to bulk-import client leads onto the map. Each lead will be attributed to your account.</p>
      </div>

      {/* Upload card */}
      <div style={styles.uploadCard}>
        <div style={styles.uploadLeft}>
          <h3 style={styles.uploadTitle}>Upload CSV File</h3>
          <p style={styles.formatInfo}>
            Required columns: <strong>Client Name</strong>, <strong>Status</strong>, <strong>Location</strong>
          </p>
          <p style={styles.formatHint}>
            Location format: <code style={styles.code}>-26.277, 28.438</code> (latitude, longitude)<br />
            Status values: New Lead, Stable Client, Unstable Client, Buying from Competitor, Technical Difficulty
          </p>
          <div style={styles.uploadActions}>
            <button onClick={downloadTemplate} style={styles.templateBtn}>
              📄 Download Template
            </button>
            <label style={styles.uploadBtn}>
              {loading ? '⏳ Parsing...' : '📂 Choose CSV File'}
              <input type="file" accept=".csv" onChange={handleFileUpload} style={{ display: 'none' }} disabled={loading} />
            </label>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      {rows.length > 0 && (
        <>
          <div style={styles.statsBar}>
            <div style={styles.stat}><span style={styles.statNum}>{stats.total}</span><span style={styles.statLabel}>Total</span></div>
            <div style={styles.stat}><span style={{ ...styles.statNum, color: '#2fb65c' }}>{stats.valid}</span><span style={styles.statLabel}>Valid</span></div>
            <div style={styles.stat}><span style={{ ...styles.statNum, color: '#e74c3c' }}>{stats.errors}</span><span style={styles.statLabel}>Errors</span></div>
            <div style={styles.stat}><span style={{ ...styles.statNum, color: '#3498db' }}>{stats.imported}</span><span style={styles.statLabel}>Imported</span></div>
            <div style={styles.stat}><span style={{ ...styles.statNum, color: '#f39c12' }}>{stats.pending}</span><span style={styles.statLabel}>Pending</span></div>
          </div>

          <div style={styles.bulkActions}>
            <button
              onClick={importAll}
              disabled={importing || stats.pending === 0}
              style={{ ...styles.importAllBtn, opacity: (importing || stats.pending === 0) ? 0.5 : 1 }}
            >
              {importing ? '⏳ Importing...' : `📤 Import All (${stats.pending})`}
            </button>
            <button onClick={clearAll} style={styles.clearBtn}>🗑️ Clear Table</button>
          </div>

          {/* Preview table */}
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>#</th>
                  <th style={styles.th}>Client Name</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Location</th>
                  <th style={styles.th}>Result</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.id} style={{
                    ...styles.tr,
                    background: row.importStatus === 'imported' ? 'rgba(47,182,92,0.10)'
                      : row.errors ? 'rgba(231,76,60,0.08)' : 'transparent'
                  }}>
                    <td style={{ ...styles.td, color: colors.textMuted, fontSize: '12px' }}>{index + 1}</td>

                    {editingId === row.id ? (
                      <>
                        <td style={styles.td}>
                          <input
                            value={editForm.clientName}
                            onChange={e => setEditForm(f => ({ ...f, clientName: e.target.value }))}
                            style={styles.editInput}
                          />
                        </td>
                        <td style={styles.td}>
                          <select
                            value={editForm.status}
                            onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}
                            style={styles.editSelect}
                          >
                            {Object.entries(STATUS_LABELS).map(([k, v]) => (
                              <option key={k} value={k}>{v}</option>
                            ))}
                          </select>
                        </td>
                        <td style={styles.td}>
                          <input
                            value={editForm.locationStr}
                            onChange={e => setEditForm(f => ({ ...f, locationStr: e.target.value }))}
                            style={styles.editInput}
                            placeholder="-26.277, 28.438"
                          />
                        </td>
                        <td style={styles.td} />
                        <td style={styles.td}>
                          <div style={styles.actionRow}>
                            <button onClick={saveEdit} style={styles.saveBtn}>Save</button>
                            <button onClick={cancelEdit} style={styles.cancelBtn}>✕</button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ ...styles.td, fontWeight: '600' }}>{row.clientName || <em style={{ color: colors.textFaint }}>—</em>}</td>
                        <td style={styles.td}>
                          <span style={{ ...styles.statusBadge, background: STATUS_COLORS[row.status] }}>
                            {STATUS_LABELS[row.status]}
                          </span>
                        </td>
                        <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: '12px', color: colors.textMuted }}>
                          {row.location
                            ? `${row.location.lat.toFixed(4)}, ${row.location.lng.toFixed(4)}`
                            : <span style={{ color: '#ff8178' }}>{row.locationStr || '—'}</span>
                          }
                        </td>
                        <td style={styles.td}>
                          {row.importStatus === 'imported' && <span style={styles.importedBadge}>✅ Imported</span>}
                          {row.importStatus === 'error' && <span style={styles.errorBadge}>❌ Failed</span>}
                          {row.errors && (
                            <ul style={styles.errorList}>
                              {row.errors.map((e, i) => <li key={i}>{e}</li>)}
                            </ul>
                          )}
                          {!row.errors && !row.importStatus && <span style={styles.pendingBadge}>Ready</span>}
                        </td>
                        <td style={styles.td}>
                          {row.importStatus !== 'imported' && (
                            <div style={styles.actionRow}>
                              <button onClick={() => startEdit(row)} style={styles.editBtn}>✏️</button>
                              <button
                                onClick={() => importSingle(row)}
                                disabled={!!row.errors || importing}
                                style={{ ...styles.importOneBtn, opacity: (row.errors || importing) ? 0.4 : 1 }}
                              >
                                Import
                              </button>
                              <button onClick={() => deleteRow(row.id)} style={styles.deleteBtn}>🗑️</button>
                            </div>
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {rows.length === 0 && !loading && (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>📭</div>
          <p>No data yet — upload a CSV file to get started.</p>
        </div>
      )}
    </div>
  );
};

const STATUS_COLORS = {
  stable: '#2fb65c',
  unstable: '#f4d03f',
  competitor: '#e74c3c',
  lead: '#ff69b4',
  technical: '#9b59b6'
};

const styles = {
  container: { padding: '24px', minHeight: '500px', maxWidth: '1100px', margin: '0 auto' },
  header: { marginBottom: '24px' },
  title: { margin: '0 0 6px 0', fontSize: '22px', color: colors.text, fontWeight: '700' },
  subtitle: { margin: 0, fontSize: '14px', color: colors.textMuted },

  uploadCard: {
    background: colors.glass, borderRadius: '12px', padding: '24px',
    border: '2px dashed rgba(47,182,92,0.4)', marginBottom: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.25)'
  },
  uploadTitle: { margin: '0 0 8px 0', fontSize: '16px', color: colors.text },
  formatInfo: { margin: '0 0 6px 0', fontSize: '13px', color: colors.text },
  formatHint: { margin: '0 0 16px 0', fontSize: '12px', color: colors.textMuted, lineHeight: 1.6 },
  code: { background: colors.surface2, padding: '1px 5px', borderRadius: '4px', fontFamily: 'monospace', color: colors.text },
  uploadActions: { display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' },
  templateBtn: {
    padding: '9px 18px', background: 'transparent', color: colors.parGreenBright,
    border: '2px solid rgba(47,182,92,0.55)', borderRadius: '8px',
    fontSize: '13px', fontWeight: '600', cursor: 'pointer'
  },
  uploadBtn: {
    padding: '9px 18px', background: '#2fb65c', color: 'white',
    border: 'none', borderRadius: '8px',
    fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'inline-block'
  },

  statsBar: {
    display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap'
  },
  stat: {
    background: colors.glass, border: `1px solid ${colors.glassBorder}`, borderRadius: '10px', padding: '12px 20px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.25)', display: 'flex',
    flexDirection: 'column', alignItems: 'center', minWidth: '70px'
  },
  statNum: { fontSize: '22px', fontWeight: '700', color: colors.text },
  statLabel: { fontSize: '11px', color: colors.textMuted, fontWeight: '600', textTransform: 'uppercase', marginTop: '2px' },

  bulkActions: { display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' },
  importAllBtn: {
    padding: '10px 22px', background: '#2fb65c', color: 'white',
    border: 'none', borderRadius: '8px', fontSize: '14px',
    fontWeight: '700', cursor: 'pointer', boxShadow: '0 2px 8px rgba(47,182,92,0.3)'
  },
  clearBtn: {
    padding: '10px 18px', background: 'transparent', color: '#ff8178',
    border: '1px solid rgba(231,76,60,0.5)', borderRadius: '8px',
    fontSize: '13px', fontWeight: '600', cursor: 'pointer'
  },

  tableWrapper: { overflowX: 'auto', borderRadius: '10px', border: `1px solid ${colors.glassBorder}`, background: colors.glass },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left', padding: '12px 14px',
    background: colors.surfaceSoft, fontWeight: '700', fontSize: '12px',
    color: colors.textMuted, borderBottom: `2px solid ${colors.border}`,
    textTransform: 'uppercase', letterSpacing: '0.4px'
  },
  tr: { borderBottom: `1px solid ${colors.borderSoft}`, transition: 'background 0.1s' },
  td: { padding: '12px 14px', fontSize: '13px', color: colors.text, verticalAlign: 'middle' },

  editInput: {
    width: '100%', padding: '6px 8px', fontSize: '13px',
    border: '2px solid rgba(47,182,92,0.55)', borderRadius: '6px', outline: 'none', boxSizing: 'border-box',
    background: colors.surface, color: colors.text
  },
  editSelect: {
    width: '100%', padding: '6px 8px', fontSize: '13px',
    border: '2px solid rgba(47,182,92,0.55)', borderRadius: '6px', background: colors.surfaceDeep, color: colors.text, cursor: 'pointer'
  },

  statusBadge: {
    display: 'inline-block', color: 'white', padding: '3px 10px',
    borderRadius: '10px', fontSize: '11px', fontWeight: '600'
  },
  importedBadge: { color: colors.parGreenBright, fontWeight: '700', fontSize: '12px' },
  errorBadge: { color: '#ff8178', fontWeight: '700', fontSize: '12px' },
  pendingBadge: {
    display: 'inline-block', background: 'rgba(58,155,212,0.18)', color: '#8fcdee',
    border: '1px solid rgba(58,155,212,0.35)', borderRadius: '8px',
    padding: '2px 8px', fontSize: '11px', fontWeight: '600'
  },
  errorList: { margin: '0', padding: '0 0 0 14px', fontSize: '11px', color: '#ff8178' },

  actionRow: { display: 'flex', gap: '6px', alignItems: 'center' },
  editBtn: {
    padding: '5px 8px', background: colors.surface, border: `1px solid ${colors.border}`,
    borderRadius: '5px', cursor: 'pointer', fontSize: '13px'
  },
  importOneBtn: {
    padding: '5px 10px', background: '#2fb65c', color: 'white',
    border: 'none', borderRadius: '5px', fontSize: '12px',
    fontWeight: '600', cursor: 'pointer'
  },
  deleteBtn: {
    padding: '5px 8px', background: 'rgba(231,76,60,0.10)', border: '1px solid rgba(231,76,60,0.4)',
    borderRadius: '5px', cursor: 'pointer', fontSize: '13px', color: '#ff8178'
  },
  saveBtn: {
    padding: '5px 12px', background: '#2fb65c', color: 'white',
    border: 'none', borderRadius: '5px', fontSize: '12px',
    fontWeight: '600', cursor: 'pointer'
  },
  cancelBtn: {
    padding: '5px 8px', background: colors.surface, border: `1px solid ${colors.border}`,
    borderRadius: '5px', cursor: 'pointer', fontSize: '13px', color: colors.textMuted
  },

  emptyState: { textAlign: 'center', padding: '60px 20px', color: colors.textMuted },
  emptyIcon: { fontSize: '56px', marginBottom: '16px' },
  uploadLeft: {}
};

export default LeadsImport;
